/**
 * Export — and an honest account of what this browser can actually encode.
 *
 * WAV is always available because PadLab writes the file itself. AAC is not: there is no
 * offline AAC encoder on the web platform, so an M4A is produced by playing the finished
 * render through `MediaRecorder` in real time. That is a genuine limitation, it takes as
 * long as the music does, and the screen says so before you commit to it rather than after.
 */

import { h, onCleanup } from '../../core/dom';
import { computed, effect, signal } from '../../core/signal';
import { icon, neonButton, segmentedRail } from '../components/controls';
import { loadingBar } from '../components/sheet';
import {
  EXPORT_FORMATS,
  EXPORT_SCOPES,
  exportFormatDisplayName,
  exportFormatFileExtension,
  exportFormatSummary,
  exportScopeDisplayName,
  padIsEmpty,
  projectAsset,
  type ExportFormat,
  type ExportScope,
  type SampleAsset,
  type SampleEdit,
} from '../../model/types';
import {
  encodeWav,
  bufferForAsset,
  engine,
  currentPattern,
  notify,
  project,
  projectStore,
  renderEdit,
  reportFailure,
  revision,
  selectedAssetID,
  selectedPadID,
  toRenderedAudio,
} from '../../app/state';
import {
  encodeM4A,
  isM4AExportSupported,
  renderPattern,
  renderStems,
  supportedM4AMimeType,
  type RenderedAudio,
} from '../../audio/offline-render';
import { canShareFiles, downloadFile, shareContent, suggestFilename } from '../../platform/share';
import { callout, formatBytes, requiresProject, screenBody, screenHeader, section } from '../../app/shell';

/**
 * What "the sound currently open in the editor" actually means.
 *
 * The editor is reached two ways and only one of them sets `selectedAssetID`: opening it
 * from a pad — the primary route — sets `selectedPadID` and writes every edit into
 * `pad.edit`. Reading `selectedAssetID` and `asset.defaultEdit` therefore refused to export
 * at all after the common journey, and exported a stale, unedited copy after the other one.
 * This is the same resolution rule the wave editor itself uses.
 */
function editorSelection(): { asset: SampleAsset; edit: SampleEdit } | null {
  const open = project.peek();
  if (open === null) return null;
  const padID = selectedPadID.peek();
  const pad = padID === null ? undefined : open.banks.flatMap((bank) => bank.pads).find((entry) => entry.id === padID);
  if (pad !== undefined && pad.assetID !== undefined) {
    const asset = projectAsset(open, pad.assetID);
    if (asset !== undefined) return { asset, edit: pad.edit };
  }
  const assetID = selectedAssetID.peek();
  const asset = assetID === null ? undefined : projectAsset(open, assetID);
  if (asset === undefined) return null;
  return { asset, edit: asset.defaultEdit };
}

const mediaSource = {
  load: async (asset: Parameters<typeof bufferForAsset>[0]): Promise<AudioBuffer> => {
    const buffer = await bufferForAsset(asset);
    if (buffer === null) throw new Error(`“${asset.name}” is missing from this device and could not be included.`);
    return buffer;
  },
};

async function deliver(blob: Blob, filename: string, title: string): Promise<void> {
  const file = new File([blob], filename, { type: blob.type });
  if (canShareFiles([file])) {
    const result = await shareContent({ title, files: [file] });
    if (result.outcome === 'shared') {
      notify('Shared', filename);
      return;
    }
    if (result.outcome === 'cancelled') return;
  }
  downloadFile(blob, filename);
  notify('Saved', `${filename} · ${formatBytes(blob.size)}`);
}

export function exportScreen(): HTMLElement {
  if (project.peek() === null) return requiresProject();

  const format = signal<ExportFormat>('wav24');
  const scope = signal<ExportScope>('mix');
  const progress = signal(0);
  const busy = signal(false);
  const statusText = signal('');
  const controller = { current: null as AbortController | null };

  const m4aAvailable = isM4AExportSupported();
  const mime = supportedM4AMimeType();

  const bar = loadingBar({ label: computed(() => statusText.value), progress: computed(() => progress.value) });

  const availableFormats = computed(() =>
    EXPORT_FORMATS.filter((entry) => entry !== 'm4a' || m4aAvailable).map((entry) => ({
      id: entry,
      title: exportFormatDisplayName(entry),
    })),
  );

  const formatRail = segmentedRail({
    items: availableFormats.peek(),
    selection: computed(() => format.value),
    label: 'Export format',
    onSelect: (id) => {
      format.value = id as ExportFormat;
    },
  });

  const scopeRail = segmentedRail({
    items: EXPORT_SCOPES.map((entry) => ({ id: entry, title: exportScopeDisplayName(entry) })),
    selection: computed(() => scope.value),
    label: 'What to export',
    onSelect: (id) => {
      scope.value = id as ExportScope;
    },
  });

  async function encode(audio: RenderedAudio, base: string): Promise<void> {
    const chosen = format.peek();
    if (chosen === 'm4a') {
      statusText.value = 'Encoding AAC in real time…';
      const context = engine.context();
      const blob = await encodeM4A(audio, {
        ...(controller.current === null ? {} : { signal: controller.current.signal }),
        ...(context === null ? {} : { context }),
        onProgress: (fraction) => {
          progress.value = 0.5 + fraction * 0.5;
        },
      });
      await deliver(blob, suggestFilename(base, mime ?? 'audio/mp4'), base);
      return;
    }
    statusText.value = 'Writing WAV…';
    const blob = encodeWav(audio, chosen === 'wav16' ? 16 : 24);
    await deliver(blob, `${base}.${exportFormatFileExtension(chosen)}`, base);
  }

  async function run(): Promise<void> {
    const open = project.peek();
    if (open === null || busy.peek()) return;
    busy.value = true;
    progress.value = 0;
    controller.current = new AbortController();
    const signalRef = controller.current.signal;
    try {
      switch (scope.peek()) {
        case 'mix': {
          statusText.value = 'Rendering the mix…';
          const audio = await renderPattern(open, {
            media: mediaSource,
            ...(currentPattern.peek() === null ? {} : { patternID: currentPattern.peek()!.id }),
            repeats: 2,
            signal: signalRef,
            onProgress: (fraction) => {
              progress.value = fraction * 0.5;
            },
          });
          await encode(audio, `${open.name} mix`);
          break;
        }
        case 'song': {
          // No `patternID`, and `song: true`. Both scopes below used to pass the current
          // pattern unconditionally, and `renderTimeline` only walks `project.song` when the
          // pattern is absent — so the arrangement could not be exported at all.
          if (open.song.sections.length === 0) {
            notify('There is no arrangement yet', 'Chain some patterns in the Song section of the Sequencer first.');
            break;
          }
          statusText.value = 'Rendering the arrangement…';
          const audio = await renderPattern(open, {
            media: mediaSource,
            song: true,
            repeats: 1,
            signal: signalRef,
            onProgress: (fraction) => {
              progress.value = fraction * 0.5;
            },
          });
          await encode(audio, `${open.name} song`);
          break;
        }
        case 'stems': {
          const wholeSong = open.song.sections.length > 0 && open.song.isEnabled;
          statusText.value = wholeSong ? 'Rendering arrangement stems…' : 'Rendering stems…';
          const stems = await renderStems(open, {
            media: mediaSource,
            ...(wholeSong
              ? { song: true as const }
              : currentPattern.peek() === null
                ? {}
                : { patternID: currentPattern.peek()!.id }),
            repeats: wholeSong ? 1 : 2,
            signal: signalRef,
            onProgress: (fraction) => {
              progress.value = fraction * 0.6;
            },
          });
          if (stems.length === 0) {
            notify('Nothing to bounce', 'This pattern has no active tracks.');
            break;
          }
          for (let index = 0; index < stems.length; index++) {
            const stem = stems[index]!;
            progress.value = 0.6 + (index / stems.length) * 0.4;
            await encode(stem.audio, `${open.name} - ${stem.name}`);
          }
          break;
        }
        case 'selectedSample': {
          const chosen = editorSelection();
          if (chosen === null) {
            notify('Pick a sound first', 'Open a sound from the Library or a pad, then come back.');
            break;
          }
          const { asset, edit } = chosen;
          statusText.value = `Rendering “${asset.name}”…`;
          const buffer = await bufferForAsset(asset);
          if (buffer === null) throw new Error('That sound could not be loaded from this device.');
          progress.value = 0.4;
          const rendered = await renderEdit(buffer, edit, { tempo: open.tempo, sourceName: asset.name });
          await encode(toRenderedAudio(rendered), asset.name);
          break;
        }
        case 'projectPackage': {
          statusText.value = 'Packaging the project…';
          const blob = await projectStore().exportPackage(open.id, {
            onProgress: (fraction) => {
              progress.value = fraction;
            },
          });
          await deliver(blob, projectStore().packageFileName(open.name), open.name);
          break;
        }
        case 'soundPack': {
          statusText.value = 'Building the sound pack…';
          // A pack is a project package with only the sounds in it, which is exactly what
          // the importer already knows how to read — no second format to maintain. The
          // filter is what makes it a different artefact: without `assetIDs` this produced
          // the same archive as "Project Package", differing only in filename.
          const packed = [
            ...new Set(
              open.banks
                .flatMap((bank) => bank.pads)
                .map((pad) => pad.assetID)
                .filter((id): id is string => id !== undefined),
            ),
          ];
          if (packed.length === 0) {
            notify('Nothing to pack', 'Put a sound on a pad first. A pack is built from the loaded pads.');
            break;
          }
          const blob = await projectStore().exportPackage(open.id, {
            assetIDs: packed,
            onProgress: (fraction) => {
              progress.value = fraction;
            },
          });
          await deliver(blob, projectStore().packageFileName(`${open.name} pack`), `${open.name} pack`);
          break;
        }
      }
      progress.value = 1;
      statusText.value = 'Done';
    } catch (error) {
      if ((error as { name?: string }).name === 'AbortError') statusText.value = 'Cancelled';
      else reportFailure('Export failed', error);
    } finally {
      busy.value = false;
      controller.current = null;
    }
  }

  const scopeDetail = h('p', { class: 'muted' });
  const stopDetail = effect(() => {
    revision.value;
    const open = project.value;
    const pattern = currentPattern.value;
    const loaded = open === null ? 0 : open.banks.flatMap((bank) => bank.pads).filter((pad) => !padIsEmpty(pad)).length;
    switch (scope.value) {
      case 'mix':
        scopeDetail.textContent = `Two passes of “${pattern?.name ?? 'the current pattern'}” at ${Math.round(open?.tempo ?? 96)} BPM, through the master chain and limiter.`;
        break;
      case 'song': {
        const sections = open?.song.sections.length ?? 0;
        scopeDetail.textContent =
          sections === 0
            ? 'No arrangement yet. Chain patterns in the Song section of the Sequencer and the whole thing becomes exportable here.'
            : `The whole arrangement: ${sections} section${sections === 1 ? '' : 's'} in order, with repeats, through the master chain and limiter.`;
        break;
      }
      case 'stems':
        scopeDetail.textContent =
          (open?.song.sections.length ?? 0) > 0 && open?.song.isEnabled === true
            ? 'One file per track across the whole arrangement, dry of the master chain so they line up in another app.'
            : `One file per track in “${pattern?.name ?? 'the current pattern'}”, dry of the master chain so they line up in another app.`;
        break;
      case 'selectedSample': {
        const chosen = editorSelection();
        scopeDetail.textContent =
          chosen === null
            ? 'Nothing is open in the editor yet. Open a sound from the Library, or a pad, and it appears here.'
            : `“${chosen.asset.name}”, with every edit and effect rendered in.`;
        break;
      }
      case 'projectPackage':
        scopeDetail.textContent = `Everything: the project file plus all ${open?.assets.length ?? 0} recordings, in one .padlab file that opens on iPhone too.`;
        break;
      case 'soundPack':
        scopeDetail.textContent = `A shareable pack built from this project's ${loaded} loaded pads.`;
        break;
    }
  });
  onCleanup(scopeDetail, stopDetail);

  const screen = h(
    'div',
    { class: 'screen screen--export' },
    screenHeader({ kicker: 'Share your work', title: 'Export' }),
    screenBody(
      section('What', scopeRail.el, scopeDetail),
      section(
        'Format',
        formatRail.el,
        h('p', { class: 'muted' }, computed(() => exportFormatSummary(format.value))),
        m4aAvailable
          ? callout(
              'How AAC is made here',
              'M4A encodes in real time',
              `There is no offline AAC encoder in a browser, so PadLab plays the finished render through the ${mime ?? 'audio/mp4'} recorder and captures it. A three-minute mix takes about three minutes. WAV is instant and lossless.`,
              'warning',
            )
          : callout(
              'Format support',
              'This browser cannot encode AAC',
              'No MediaRecorder AAC profile is available here, so M4A is hidden rather than offered and failed. WAV is lossless and opens everywhere.',
              'warning',
            ),
      ),
      section(
        'Go',
        bar.el,
        h(
          'div',
          { class: 'export-actions' },
          neonButton({
            title: computed(() => (busy.value ? 'Working…' : 'Export')),
            style: 'primary',
            fullWidth: true,
            enabled: computed(() => !busy.value),
            onPress: () => void run(),
          }).el,
          neonButton({
            title: 'Cancel',
            style: 'ghost',
            enabled: computed(() => busy.value),
            onPress: () => controller.current?.abort(),
          }).el,
        ),
        h(
          'p',
          { class: 'kicker export-note' },
          canShareFiles([new File([new Blob()], 'probe.wav', { type: 'audio/wav' })])
            ? 'Finished files open the system share sheet'
            : 'Finished files download to your Files app',
        ),
      ),
      section(
        'Everything in one place',
        h(
          'div',
          { class: 'export-hints' },
          h('p', null, icon('info.circle.fill', { size: 15 }), ' A project backup is the only format that keeps pads, patterns and recordings together.'),
          h('p', null, icon('info.circle.fill', { size: 15 }), ' Stems are rendered without the master chain so they add back up to the mix.'),
          h('p', null, icon('info.circle.fill', { size: 15 }), ' Nothing is uploaded: exports are produced on this device and handed to you.'),
        ),
      ),
    ),
  );

  onCleanup(screen, () => {
    controller.current?.abort();
    formatRail.destroy();
    scopeRail.destroy();
    bar.destroy();
  });
  return screen;
}
